import {
  IPathNodeCustomization,
  IPatNodeCustomizationWholePath,
  ISankeyNode,
  NodeCustomizationScope,
} from './interfaces';
import { MaybeNil } from '../../../utils/interfaces';
import { isNil } from 'lodash';
import { OthersConfigAction } from '../interfaces';
import TwoStateSwitch, { TwoStateOption } from '../../TwoStateSwitch';
import styled, { css } from 'styled-components';
import { getScrollBarStyles } from '../../../config/styles';
import { IStateHint } from '../../../utils/stateHint';
import { isMergedNode } from './utils';
import { floatingComponentStyles } from '../../../styles/shareableStyles';
import { StyledSingleSelectOption } from '../../Dropdown/SingleSelect';
import KubitTooltip from '../../KubitTooltip';
import { theme } from '../../../styles/theme';
import ThinDivider from '../../ThinDivider';
import { formatNumberWithComma } from '../../../utils/formatText';

interface IProps {
  onCreateFunnel: () => void;
  onNodeCustomization: (customization: IPathNodeCustomization) => void;
  customizationScope: NodeCustomizationScope;
  setCustomizationScope: (scope: NodeCustomizationScope) => void;
  canCreateFunnel: IStateHint;
  observedNodeIndex: MaybeNil<number>;
  nodes: ISankeyNode[];
  onLockObservedNode: () => void;
}

const Container = styled.div`
  max-height: 400px;
  ${floatingComponentStyles};
  ${getScrollBarStyles(2)};
  overflow-y: auto;
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.15);
  border-radius: 8px;
`;
const CustomMenuItem = styled.div<{ $isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  column-gap: 6px;
  padding: 6px 8px;
  color: ${theme.colors.secondary900};
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  border-bottom: 1px solid ${theme.colors.border};
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `}
`;

const customizationScopeOptions: [
  TwoStateOption<NodeCustomizationScope>,
  TwoStateOption<NodeCustomizationScope>,
] = [
  {
    label: 'Step',
    value: NodeCustomizationScope.IN_THIS_STEP,
  },
  {
    label: 'Global',
    value: NodeCustomizationScope.IN_WHOLE_PATH,
  },
];

const SankeyNodeContextMenu = ({
  onCreateFunnel,
  canCreateFunnel,
  nodes,
  observedNodeIndex,
  onNodeCustomization,
  customizationScope,
  setCustomizationScope,
  onLockObservedNode,
}: IProps) => {
  const observedNode = !isNil(observedNodeIndex)
    ? nodes[observedNodeIndex]
    : null;
  const hasObservedNode = !isNil(observedNode) && observedNode.seq > 0;

  if (hasObservedNode) {
    if (isMergedNode(observedNode)) {
      return (
        <Container>
          <CustomMenuItem>
            <div>Separate from &quot;{observedNode.name}&quot;</div>
            <ThinDivider layout="vertical" size="short" />
            <TwoStateSwitch
              selectedValue={customizationScope}
              options={customizationScopeOptions}
              onChange={setCustomizationScope}
            />
          </CustomMenuItem>
          {observedNode.mergedNodes.map((node) => {
            const data: IPathNodeCustomization =
              customizationScope === NodeCustomizationScope.IN_THIS_STEP
                ? {
                    scope: customizationScope,
                    type: OthersConfigAction.SEPARATE,
                    name: node.name,
                    seq: node.seq,
                  }
                : {
                    scope: customizationScope,
                    type: OthersConfigAction.SEPARATE,
                    name: node.name,
                  };

            // indent slightly the events per comment in
            // https://kubitai.atlassian.net/browse/KB-5576?focusedCommentId=22806
            return (
              <StyledSingleSelectOption
                key={node.key}
                onClick={() => onNodeCustomization(data)}
              >
                {`${node.name} (${formatNumberWithComma(node.incomingCount)})`}
              </StyledSingleSelectOption>
            );
          })}
        </Container>
      );
    }

    const combineIntoOthersInCurrStep = {
      scope: customizationScope,
      type: OthersConfigAction.COMBINE,
      name: observedNode.name,
      seq: observedNode.seq,
    };

    const combineIntoOthersInWholePath: IPatNodeCustomizationWholePath = {
      scope: NodeCustomizationScope.IN_WHOLE_PATH,
      type: OthersConfigAction.COMBINE,
      name: observedNode.name,
    };

    return (
      <Container>
        <StyledSingleSelectOption
          // show path only need to close the menu
          iconName="path"
          onClick={onLockObservedNode}
        >
          Select Path
        </StyledSingleSelectOption>
        <KubitTooltip
          disabled={!canCreateFunnel.isDisabled}
          content={
            canCreateFunnel.isDisabled ? canCreateFunnel.hint : undefined
          }
        >
          <StyledSingleSelectOption
            iconName="funnel"
            onClick={onCreateFunnel}
            disabled={canCreateFunnel.isDisabled}
          >
            Create Funnel
          </StyledSingleSelectOption>
        </KubitTooltip>
        <StyledSingleSelectOption
          iconName="intersect"
          onClick={() => onNodeCustomization(combineIntoOthersInCurrStep)}
        >
          {`Combine with 'Others'`}
        </StyledSingleSelectOption>
        <StyledSingleSelectOption
          iconName="global-simple"
          onClick={() => onNodeCustomization(combineIntoOthersInWholePath)}
        >
          {`Combine with 'Others' globally`}
        </StyledSingleSelectOption>
      </Container>
    );
  }

  return null;
};

export default SankeyNodeContextMenu;
